import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';
import { useBasket } from '~/features/basket';
import { BasketEmpty } from '~/features/basket/components/BasketEmpty';
import { BasketList } from '~/features/basket/components/BasketList';
import { BasketSummary } from '~/features/basket/components/BasketSummary/BasketSummary';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { P90BasketPage as BasketType, Modules } from '~/lib/data-contract';
import { PaymentResponseCode } from '~/lib/data-contract/payment';
import { Faq } from '~/shared/components/Summary/components/Faq';
import { USPs } from '~/shared/components/Summary/components/USPs';
import { UspFaqWrapper } from '~/shared/components/UspFaqWrapper/UspFaqWrapper';
import { PaymentMethod } from '~/shared/constants';
import { useEvents } from '~/shared/hooks/useEvents';
import { useFrame } from '~/shared/utils';
import { getQueryValue } from '~/shared/utils/get-query-value';
import { useTranslation } from '~/shared/utils/translation';
import { DynamicBlockList } from '~/templates/blocks';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import styles from '../../P90BasketPage/P90BasketPage.module.scss';
import { BasketSkeleton } from '../../P90BasketPage/components/BasketSkeleton/BasketSkeleton';
import {
    StyleBasketList,
    StyleBasketSummary,
    StyledBasketContent,
    StyledContent,
} from '../../P90BasketPage/styled';

type P90BasketPageProps = BasketType;

export const P90BasketPage1 = (props: P90BasketPageProps) => {
    const router = useRouter();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const basket = useBasket();
    const { isInitialized, isGuest } = useCustomer();
    const { identifyBasketIdEvent } = useEvents(frame);
    const { membership, setMembership } = useLocalBasketState();
    const {
        data: { market },
    } = useFrame();
    const isInit = useRef(false);

    const {
        data: { settings },
    } = useFrame();

    const onBasketClick = () => {
        router.push(settings?.pageReferences.checkoutPage?.url || '/');
    };

    const hasBasketLoaded = !!basket.basketId;

    const basketIsEmpty = useMemo(() => {
        return basket.itemCount() <= 0;
    }, [basket]);

    useEffect(() => {
        identifyBasketIdEvent(basket.basketId);
    }, [basket.basketId]);

    useEffect(() => {
        if (isInit.current) return;
        if (isInitialized && basket.basketId && isGuest) {
            setMembership(true);
            isInit.current = true;
        }
    }, [isInitialized, isGuest, basket, isInit, setMembership, membership]);

    const hasDoneBasketPrecheck = useRef(false);

    /**
     * We will remove Nets/AltaPay payment instruments when we get an error during a transaction
     * or when we get "cancel" as the response code.
     *
     * This will help prevent mixed payment instruments if other preventative mechanisms fail (i.e., Payment.tsx).
     */
    useEffect(() => {
        if (!basket.basketId || hasDoneBasketPrecheck.current) return;

        const hasFailedPayment =
            getQueryValue(router.query.error) ||
            getQueryValue(router.query.responseCode)?.toLowerCase() === PaymentResponseCode.cancel;

        if (hasFailedPayment) {
            const removePaymentInstruments = async () => {
                if (!basket.paymentInstruments?.length) return;

                /**
                 * We will iterate through all the payment instruments to make sure we've removed all of them.
                 */
                for (const paymentInstrument of basket.paymentInstruments) {
                    switch (paymentInstrument.paymentMethodId) {
                        case PaymentMethod.ALTAPAY:
                            await basket.removePaymentMethodFromBasket(PaymentMethod.ALTAPAY);
                            break;

                        case PaymentMethod.NETS:
                            await basket.removePaymentMethodFromBasket(PaymentMethod.NETS);
                            break;
                    }
                }
            };

            removePaymentInstruments();
        }

        hasDoneBasketPrecheck.current = true;
    }, [basket.basketId]);

    return (
        <>
            <DynamicBlockList elements={props.pageElements as Modules[]} />
            <ModuleContainer spacingTop="none">
                <StyledContent>
                    <StyledBasketContent>
                        {!hasBasketLoaded || !isInitialized || !isInit.current ? (
                            <BasketSkeleton />
                        ) : basketIsEmpty ? (
                            <BasketEmpty />
                        ) : (
                            <>
                                <StyleBasketList>
                                    <BasketList membership={membership} />
                                </StyleBasketList>

                                <StyleBasketSummary>
                                    <BasketSummary
                                        onSubmit={onBasketClick}
                                        disabledButton={basketIsEmpty}
                                        membership={membership}
                                        setMembership={setMembership}
                                    />
                                    <UspFaqWrapper>
                                        {market?.checkoutUsps && (
                                            <USPs usps={market.checkoutUsps} />
                                        )}
                                        {market?.checkoutFaq && (
                                            <Faq
                                                className={styles.faq}
                                                title={translate('checkout.faqSection.title')}
                                                faqs={market.checkoutFaq}
                                            />
                                        )}
                                    </UspFaqWrapper>
                                </StyleBasketSummary>
                            </>
                        )}
                    </StyledBasketContent>
                </StyledContent>
            </ModuleContainer>

            <DynamicBlockList elements={props.bottomPageElements as Modules[]} />
        </>
    );
};
